/**
 * Agent ticket detail query.
 *
 * Wraps GET /api/v1/agent/tickets/{number}. Includes soft-deleted rows
 * (the backend deliberately returns them for the agent view). The poll
 * interval is set by the consumer; default is 15 seconds, matching the
 * customer-side detail polling cadence.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import api from "@/lib/api";
import type {
  AttachmentManifest,
  AttachmentResponse,
  TicketResponse,
} from "@/types/api";

/**
 * Shared prefix for every cached agent-ticket detail entry. Use this
 * for invalidate / setQueriesData calls from mutation hooks so the
 * write hits the active query no matter which identifier shape the
 * page used to register it (URL code string vs. numeric ticket_number).
 * The 2026-05-17 URL refactor made detail keys hash on lowercase code,
 * but the mutations were still invalidating by ticket_number - the
 * mismatch is why status/priority changes flashed emerald-green and
 * then kept showing the old value until the user refreshed.
 */
export const agentTicketDetailQueryPrefix = [
  "agent",
  "tickets",
  "detail",
] as const;

export function agentTicketDetailQueryKey(
  code: string | number,
): readonly unknown[] {
  const key = typeof code === "string" ? code.toLowerCase() : code;
  return [...agentTicketDetailQueryPrefix, key] as const;
}

export function agentTicketAttachmentsQueryKey(
  ticketNumber: number,
): readonly unknown[] {
  return ["agent", "tickets", "attachments", ticketNumber] as const;
}

export function agentTicketManifestQueryKey(
  ticketNumber: number,
): readonly unknown[] {
  return ["agent", "tickets", "manifest", ticketNumber] as const;
}

export interface UseAgentTicketDetailOptions {
  enabled?: boolean;
  refetchIntervalMs?: number | false;
}

/**
 * Agent-side ticket detail fetch. ``code`` is the user-facing
 * identifier from the URL - matches the resolver shape on the
 * customer-side useTicketDetail (native display_code / F-N form /
 * bare ticket_number). The backend resolves any of the three via
 * get_ticket_by_public_code.
 */
export function useAgentTicketDetail(
  code: string,
  options: UseAgentTicketDetailOptions = {},
): UseQueryResult<TicketResponse> {
  return useQuery({
    queryKey: agentTicketDetailQueryKey(code),
    enabled: options.enabled !== false && code.length > 0,
    refetchInterval: options.refetchIntervalMs ?? 15_000,
    queryFn: async () => {
      const response = await api.get<TicketResponse>(
        `/agent/tickets/${encodeURIComponent(code)}`,
      );
      return response.data;
    },
  });
}

/**
 * Agent attachments query. Hits the agent-side endpoint which returns
 * every attachment on the ticket (including ones tied to private
 * comments) without the internal-note scrub the customer endpoint
 * applies.
 */
export function useAgentTicketAttachments(
  ticketNumber: number,
  options: UseAgentTicketDetailOptions = {},
): UseQueryResult<AttachmentResponse[]> {
  return useQuery({
    queryKey: agentTicketAttachmentsQueryKey(ticketNumber),
    enabled: options.enabled !== false,
    refetchInterval: options.refetchIntervalMs ?? 15_000,
    queryFn: async () => {
      const response = await api.get<AttachmentResponse[]>(
        `/agent/tickets/${ticketNumber}/attachments`,
      );
      return response.data;
    },
  });
}

/**
 * Agent download-URL fetcher. Hits the agent endpoint that returns a
 * short-lived signed Spaces URL as JSON. The frontend uses this rather
 * than a plain anchor href because anchor clicks don't carry the JWT
 * Bearer token (it lives in localStorage and is injected by the axios
 * interceptor). The returned URL points at DO Spaces directly, so the
 * actual bytes never go through our backend.
 */
export async function fetchAgentAttachmentDownloadUrl(
  ticketNumber: number,
  attachmentId: string,
): Promise<string> {
  const response = await api.get<{ url: string }>(
    `/agent/tickets/${ticketNumber}/attachments/${attachmentId}/download-url`,
  );
  return response.data.url;
}

/**
 * Upload a single attachment via multipart/form-data. ``commentId`` is
 * optional - omit it for a ticket-level file (the new-ticket form
 * path), supply it to link the file to a specific reply or note.
 *
 * One POST per file rather than one POST per N files: errors surface
 * per-attachment so a single rejected file (too large, banned MIME)
 * does not roll back a successful upload alongside it.
 */
export interface UploadAgentAttachmentInput {
  file: File;
  commentId?: string | null;
}

export function useUploadAgentAttachment(
  ticketNumber: number,
): UseMutationResult<AttachmentResponse, Error, UploadAgentAttachmentInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, commentId }: UploadAgentAttachmentInput) => {
      const form = new FormData();
      form.append("file", file);
      if (commentId) form.append("comment_id", commentId);
      // Setting Content-Type to undefined makes axios fall back to the
      // multipart boundary it generates from FormData. Leaving it as
      // the default "application/json" sends a broken multipart body.
      const response = await api.post<AttachmentResponse>(
        `/agent/tickets/${ticketNumber}/attachments`,
        form,
        { headers: { "Content-Type": undefined as unknown as string } },
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: agentTicketAttachmentsQueryKey(ticketNumber),
      });
    },
  });
}

/**
 * "Attach to Response" onto an unsent AI draft. The draft comment row
 * already exists, so the backend clones the file onto it right away
 * (shared Spaces bytes) instead of waiting for a send like the manual
 * composer's staging strip does.
 */
export interface AttachExistingToDraftInput {
  commentId: string;
  attachmentId: string;
}

export function useAttachExistingToDraft(
  ticketNumber: number,
): UseMutationResult<AttachmentResponse, Error, AttachExistingToDraftInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ commentId, attachmentId }: AttachExistingToDraftInput) => {
      const response = await api.post<AttachmentResponse>(
        `/agent/tickets/${ticketNumber}/comments/${commentId}/attach-existing`,
        { attachment_id: attachmentId },
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: agentTicketAttachmentsQueryKey(ticketNumber),
      });
    },
  });
}

/**
 * Hard-delete an attachment. The backend drops the row immediately and
 * (best-effort) the underlying Spaces object - unless another row
 * (a "clone" from Attach-to-Response) still references the same bytes,
 * in which case the object stays and only this row goes.
 *
 * Invalidates both the attachments list and the ticket detail (the
 * detail caches comment-attached rows in its embedded comment payload).
 */
export function useDeleteAgentAttachment(
  ticketNumber: number,
): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (attachmentId: string) => {
      await api.delete(
        `/agent/tickets/${ticketNumber}/attachments/${attachmentId}`,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: agentTicketAttachmentsQueryKey(ticketNumber),
      });
      void queryClient.invalidateQueries({
        queryKey: agentTicketDetailQueryPrefix,
      });
      void queryClient.invalidateQueries({
        queryKey: agentTicketManifestQueryKey(ticketNumber),
      });
    },
  });
}

/**
 * The ticket's document manifest (GET .../attachments/manifest). Always
 * current - the backend derives it live from the attachment rows. Powers
 * the Manifest tile in the Documents container so an agent can see
 * exactly what an AI agent reads over the public API.
 */
export function useAgentTicketAttachmentManifest(
  ticketNumber: number,
  options: UseAgentTicketDetailOptions = {},
): UseQueryResult<AttachmentManifest> {
  return useQuery({
    queryKey: agentTicketManifestQueryKey(ticketNumber),
    enabled: options.enabled !== false,
    refetchInterval: options.refetchIntervalMs ?? 15_000,
    queryFn: async () => {
      const response = await api.get<AttachmentManifest>(
        `/agent/tickets/${ticketNumber}/attachments/manifest`,
      );
      return response.data;
    },
  });
}

/**
 * Set or clear an attachment's manifest description (PATCH). Used by the
 * inline-edit affordance on a Documents tile. Invalidates the
 * attachments list AND the manifest so both reflect the new text.
 */
export interface SetAttachmentDescriptionInput {
  attachmentId: string;
  description: string | null;
}

export function useSetAttachmentDescription(
  ticketNumber: number,
): UseMutationResult<AttachmentResponse, Error, SetAttachmentDescriptionInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      attachmentId,
      description,
    }: SetAttachmentDescriptionInput) => {
      const response = await api.patch<AttachmentResponse>(
        `/agent/tickets/${ticketNumber}/attachments/${attachmentId}`,
        { description },
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: agentTicketAttachmentsQueryKey(ticketNumber),
      });
      void queryClient.invalidateQueries({
        queryKey: agentTicketManifestQueryKey(ticketNumber),
      });
    },
  });
}
